<template>
  <i
    class="tm-audio-menu"
    :class="{
      // not v-if - cause of fade animation
      v: isRelevant,
    }"
    :style="styleObj"
  >
    <i class="-shadow">
      <i class="-balloon">
        <i
          class="-button -tune1"
          title="Transcript"
          @mouseup.left.stop="$_menuEditAudio"
        >
          <i class="wticon wticon-Transcript" />
        </i>

        <i class="-divider" />

        <i
          class="-button -tune2"
          :class="{ '-disabled': isLastAudioTrack }"
          title="Select this and the following clips"
          @mouseup.left.stop="$_menuMultiselect"
        >
          <i class="wticon wticon-Multiselect" />
        </i>

        <i class="-divider" />

        <i
          class="-button -tune3"
          title="Delete"
          @mouseup.left.stop="$_menuRemoveAudio"
        >
          <i class="wticon wticon-trash-line" />
        </i>
      </i>
    </i>
  </i>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import _ from 'lodash-extended';
import tmBus from 'v/Story/Timeline/api/tmBus';

export default {
  name: 'tm-audio-menu',

  props: {
    hoveredRectangle: { type: Object, required: false, default: null },
  },

  data() {
    return {
      lastRectangle: null,
      activeRectangle: null,
    };
  },

  computed: {
    ...mapGetters('timemachine', ['isDndMode']),
    ...mapState('timemachine', {
      track: (state) => state.selected.audio,
      cssVariables: (state) => state.cssVariables,
      axis: (state) => state.viewOffsets.axis,
      trackLayer: (state) => state.trackLayer,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
    }),

    isRelevant() {
      return (
        this.track &&
        !this.isDndMode &&
        !this.MODE_OPTIONAL_AUDIO_INSERT &&
        !this.MODE_AUDIO_MULTISELECT
      );
    },

    trackRectangles() {
      return this.track?.rectangles ?? [];
    },

    styleObj() {
      if (!this.$data.activeRectangle || this.isDndMode) {
        return;
      }

      const point = this.$data.activeRectangle.p1.clone();
      const rightEdge = this.axis.width - this.cssVariables.pxAxisSidePadding;
      const menuRightX = point.x + this.cssVariables.pxAudioMenuW;

      if (menuRightX > rightEdge) {
        const shiftLeft = menuRightX - rightEdge;
        point.shift(-shiftLeft, 0);
      }

      return {
        transform: `translate3d(${point.x}px, ${point.y}px, 0)`,
      };
    },

    trackIndex() {
      return this.track ? this.trackLayer.audio.indexOf(this.track) : -1;
    },

    isLastAudioTrack() {
      return this.trackIndex === this.trackLayer.audio.length - 1;
    },
  },

  watch: {
    hoveredRectangle(rectangle) {
      if (this.$_trackContains(rectangle)) {
        this.$data.activeRectangle = rectangle;
      } else {
        this.$data.lastRectangle = rectangle;
      }
    },

    trackRectangles: {
      deep: true,
      handler() {
        if (this.track) {
          if (this.$_trackContains(this.$data.lastRectangle)) {
            this.$data.activeRectangle = this.$data.lastRectangle;
          } else {
            this.$data.activeRectangle = _.first(this.track.rectangles);
          }
        } else {
          this.$data.activeRectangle = null;
        }
      },
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'REMOVE_AUDIO',
      'UX_AFTER_REMOVE_AUDIO',
      'SELECT_AUDIO',
      'AUDIO_MULTISELECT_ENTER',
      'SET_MODE',
    ]),

    $_trackContains(rectangle) {
      let rv = false;

      if (this.track && rectangle) {
        rv = this.track.rectangles.indexOf(rectangle) >= 0;
      }

      return rv;
    },

    $_menuEditAudio: _.debounce(
      function () {
        this.SET_MODE({ name: 'SECONDHAND_CUT', value: false });

        tmBus.$emit(
          'callbackEditTranscript',
          this.track,
          app.TRANSCRIPT_OPENER.TIMELINE_VO
        );
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuRemoveAudio: _.debounce(
      function (e) {
        this.REMOVE_AUDIO(this.track).then((res) => {
          this.UX_AFTER_REMOVE_AUDIO({
            removedTrackIndex: res.trackIndex,
          }).then((res) => {
            tmBus.$emit('callbackAudioRemoved', {
              e,
              seekToTime: res.seekToTime,
            });
          });
        });
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuMultiselect: _.debounce(
      function (e) {
        this.AUDIO_MULTISELECT_ENTER({ index: this.trackIndex });
      },
      256,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss">
.tm-tracks > .tm-audio-menu {
  --menuHeight: 40px;
  --buttonWidth: 50px;

  position: absolute;
  top: var(--tm-pxAudioTopOffset);
  z-index: var(--tm-zTrackItemMenu);
  margin-top: calc(-1 * var(--menuHeight) - 3px);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease-in;
  white-space: nowrap;
  display: flex;

  &.v {
    visibility: visible;
    opacity: 1;
  }

  > .-shadow {
    box-shadow: -1px 1px 10px $color-black-l;
    display: flex;

    > .-balloon {
      display: flex;
      flex-direction: row;
      align-content: center;
      align-items: center;
      width: var(--tm-pxAudioMenuW);
      height: var(--menuHeight);
      background-color: #fff;
      border: 1px solid #ccc;

      > .-divider {
        border-right: 1px solid $color-black-l;
        height: 100%;
        width: 0;
      }

      > .-button {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        cursor: pointer;
        height: calc(var(--menuHeight) - 2px);
        width: var(--buttonWidth);
        color: $color-normal;

        &.-disabled {
          pointer-events: none;
          color: var(--tm-btnDisabledColour);
        }

        &:hover {
          background-color: $color-black-l2;
        }

        &.-tune1 {
          .wticon {
            font-size: 18px;
          }
        }
        &.-tune2 {
          .wticon {
            font-size: 14px;
          }
        }
        &.-tune3 {
          .wticon {
            font-size: 18px;
          }
        }
      }
    }
  }
}
</style>
